import UserContext from "Context/UserProvider";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useApiService } from '../../../services/ApiService/ApiService';

import { 
    Box,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Stack,
    CircularProgress,
    Autocomplete,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText
} from '@mui/material';

// const LoaderExampleInlineCentered = () => <Loader size='big' active inline='centered' />

function AddBusinessTabScreen(props: any) {
    const [active, setActive] = useState(true);
    const { userInfo, setUserInfo } = useContext(UserContext);
    const [isError, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const categories = checking_null_undefined(props.categories);
    const [ocpiProviders, setOcpiProviders] = useState<OcpiProvider[]>([]);
    const [ocpiProviderValue, setOcpiProviderValue] = useState<OcpiProvider | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [businessName, setBusinessName] = useState('');
    const [fleetName, setFleetName] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [partyId, setPartyId] = useState('');
    const { fetchWithAuth } = useApiService();


    const [selectedCategory, setSelectedCategory] = useState({ 
        label: "CHARGE_POINT_OPERATOR", 
        value: "CHARGE_POINT_OPERATOR", 
        fleet: false 
    });
    const [fleetRequired, setFleetRequired] = useState<boolean | null>(null);
    const [ocpiProviderRequired, setOcpiProviderRequired] = useState<boolean>(false);

    interface OcpiProvider {
        label: string;
        value: string;
        name?: string;
    }

    function checking_null_undefined(array: any) {
        if (array === null || array === undefined) {
            return [];
        }
        return array;
    }

    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage, {
                position: 'bottom-center',
                duration: 5000,
            });
        }
        setErrorMessage(null);
    }, [errorMessage]);

    useEffect(() => {
        const initializeData = async () => {
            try {
                const response = await fetchWithAuth('/ocpi/providers/get', {
                    method: 'POST',
                    body: JSON.stringify({
                        uuid: userInfo.uuid,
                        accountUuid: userInfo.accountUuid,
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error("Failed to fetch OCPI providers:", errorData);
                    toast.error("Failed to load OCPI providers.", {
                        position: 'bottom-center',
                        duration: 5000,
                    });
                    setLoading(false);
                    return;
                }

                const res_json = await response.json();
                const providers = res_json.ocpiProviders.map((e: any) => ({
                    ...e,
                    label: e.name,
                    value: e.uuid,
                }));
                setOcpiProviders(providers);

                if (providers.length > 0) {
                    setOcpiProviderValue(providers[0]);
                }
            } catch (error: any) {
                console.error("Unexpected error fetching OCPI providers:", error);
                toast.error("Unexpected error loading OCPI providers.", {
                    position: 'bottom-center',
                    duration: 5000,
                });
            } finally {
                setLoading(false);
            }
        };

        initializeData();
    }, []);

    useEffect(() => {
        setOcpiProviderRequired(selectedCategory.value === "CHARGE_POINT_OPERATOR");
    }, []);

    const businessCategoryOptions = categories.map((e: any) => ({
        label: e.name,
        value: e.name,
        fleet: e.fleetRequired,
    }));

    const navigate = useNavigate();

    const handleCategoryChange = (event: any, newValue: any) => {
        if (newValue) {
            setSelectedCategory(newValue);
            setFleetRequired(newValue.fleet);
            setOcpiProviderRequired(newValue.value === "CHARGE_POINT_OPERATOR");
        }
    };

    const handleOcpiProviderChange = (event: any, newValue: OcpiProvider | null) => {
        setOcpiProviderValue(newValue);
    };

    const registerBusiness = async () => {
        setLoading(true);
    
        const bodyData: { [key: string]: any } = {
            uuid: userInfo.uuid,
            accountUuid: userInfo.accountUuid,
            businessName: businessName,
            fleetName: fleetName,
            makeActiveOnceCreated: active,
            selectedCategory: selectedCategory.value,
        };
        
        // Only include OCPI-specific fields for CPOs
        if (selectedCategory.value === "CHARGE_POINT_OPERATOR") {
            bodyData.selectedOcpiProvider = ocpiProviderValue?.value ?? null;
            bodyData.countryCode = countryCode;
            bodyData.partyId = partyId;
        }
    
        try {
            const response = await fetchWithAuth('/business/create', {
                method: 'POST',
                body: JSON.stringify(bodyData)
            });
    
            const res_json = await response.json();
    
            if (res_json.code !== "SUCCESS") {
                switch (res_json.code) {
                    case "BUSINESS_NAME_DUPLICATE":
                        setErrorMessage("You have created a duplicate business name. Please try a new one.");
                        break;
                    case "MANDATORY_DATA_MISSING":
                        setErrorMessage("Business name is required. Please fill it in and try again.");
                        break;
                    case "OCPI_PROVIDER_ALREADY_EXISTS":
                        setErrorMessage("CPO already exists for Country Code and Party Id");
                        break;
                    default:
                        setErrorMessage("An unexpected error occurred.");
                        break;
                }
                setLoading(false);
                return;
            }
    
            if (fleetRequired && active) {
                setUserInfo((prev: any) => ({
                    ...prev,
                    businessCategory: res_json.business.chargingCategory,
                    businessProducts: res_json.business.products,
                    businessName: businessName,
                    businessUuid: res_json.business.uuid,
                    fleetUuid: res_json.business.fleets?.[0]?.uuid ?? null,
                    currentFleetName: res_json.business.fleets?.[0]?.name ?? null,
                }));
                navigate("/plan");
            }
    
            setBusinessName('');
            setFleetName('');
            setCountryCode('');
            setPartyId('');
    
            props.fetchBusinessInfo();
            setLoading(false);
    
        } catch (error) {
            console.error("Unexpected error:", error);
            toast.error("Something went wrong. Please try again.", {
                position: "bottom-center",
                duration: 5000,
            });
            setLoading(false);
        }
    };
    
    return (
        <Box>
            {loading && (
                <Box 
                    position="fixed" 
                    top={0} 
                    left={0} 
                    right={0} 
                    bottom={0} 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center"
                    bgcolor="rgba(0, 0, 0, 0.5)"
                    zIndex={9999}
                >
                    <CircularProgress />
                </Box>
            )}
            
            <Stack spacing={3}>
                <TextField
                    fullWidth
                    label="Business Name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    required
                />

                <Autocomplete
                    options={businessCategoryOptions}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Business Category"
                            required
                        />
                    )}
                />

                {fleetRequired && (
                    <TextField
                        fullWidth
                        label="Fleet Name"
                        value={fleetName}
                        onChange={(e) => setFleetName(e.target.value)}
                        required
                    />
                )}

                {ocpiProviderRequired && (
                    <>
                        <Autocomplete
                            options={ocpiProviders}
                            value={ocpiProviderValue}
                            onChange={handleOcpiProviderChange}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="OCPI Provider"
                                    required
                                />
                            )}
                        />
                        <TextField
                            fullWidth
                            label="Country Code"
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Party ID"
                            value={partyId}
                            onChange={(e) => setPartyId(e.target.value)}
                            required
                        />
                    </>
                )}

                <FormControlLabel
                    control={
                        <Switch
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Make active once created"
                />

                <Button
                    variant="contained"
                    onClick={registerBusiness}
                    disabled={loading}
                    fullWidth
                >
                    {loading ? <CircularProgress size={24} /> : 'Create Business'}
                </Button>
            </Stack>
        </Box>
    );
}

export default AddBusinessTabScreen;
